import {Component, forwardRef, Input, OnDestroy} from '@angular/core';
import { AvailabilityMacroStatus } from '../../domain/infra/availabilityMacroStatus';
import { getPriorityOfAvailabilityMacroStatus } from '../../domain/infra/availabilityMacroStatus';
import { PlugTypeChoice } from '../../domain/infra/plug/plugTypeChoice';
import { PlugTypeDto } from '../../domain/infra/plug/plugType';
import { UserCableRequirement } from '../../domain/infra/plug/plugType';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {GhostComponent} from '../../utils/ghostComponent';
import {chunk, find, memoize, orderBy} from 'lodash-es';

@Component({
    selector: 'plug-type-choice',
    templateUrl: './plugTypeChoice.component.html',
    styleUrls: ['./plugTypeChoice.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PlugTypeChoiceComponent),
            multi: true
        }
    ]
})
export class PlugTypeChoiceComponent extends GhostComponent implements ControlValueAccessor, OnDestroy {
    private plugTypeChoice: PlugTypeChoice;
    private onChange: (_: PlugTypeChoice) => void;
    private onTouched: () => void;

    @Input()  favoritePlugTypes: PlugTypeDto[];
    @Input()  doesSelectFirstByDefault: boolean = false;
    @Input()  disabled: boolean = false;
    @Input()  displayMode: ('list' | 'slider' | 'slider_minimal') = 'list';
    private _plugTypes: Array<PlugTypeDto>;  // It is an @Input

    public maybeNeedCable: boolean;
    public noCableNeeded: boolean = true;

    public nbPlugsPerSlide: number = 3;

    private readonly _memoizedChunkFunction: any;

    @Input()
    public get plugTypes(): Array<PlugTypeDto> {
        return this._plugTypes;
    }

    public set plugTypes(plugTypes: PlugTypeDto[]) {
        // Sorting rule: https://unison.4sh.fr/display/SODMBP/BR-App-Affichage+des+prises
        this._plugTypes = orderBy(plugTypes,
            [(plugType: PlugTypeDto) => this.getPlugPriority(plugType), 'maxPowerInW'],
            ['desc', 'desc']
        );

        this.ghostMode = false;

        if (this.doesSelectFirstByDefault && this.plugTypes.length > 0) {
            // wait a tick before call parent, this let Angular finish the change detection operations
            setTimeout(() => this.selectPlugType(this.plugTypes[0]), 0);
        }
    }

    getPlugPriority(plugType: PlugTypeDto): number {
        let priority = getPriorityOfAvailabilityMacroStatus(plugType.status);

        if (find(this.favoritePlugTypes, favoritePlugType => favoritePlugType.name === plugType.name)) {
            priority += 100;
        }

        return priority;
    }

    constructor() {
        super();

        this.ghostMode = true;

        this._memoizedChunkFunction = memoize(<T>(data: Array<T>, size: number): Array<Array<T>> => {
            if (!data) {
                return [];
            }

            return chunk(data, size);
        }
        );
    }

    generateGhost(): void {
        const plugTypeDto: PlugTypeDto = {
            availableOtp: true,
            availableRemoteStart: true,
            iconRef: undefined,
            iconUrl: undefined,
            maxPowerInW: 0,
            name: '',
            status: AvailabilityMacroStatus.AVAILABLE,
            userCableRequirement: UserCableRequirement.MAYBE
        };

        this._plugTypes = [plugTypeDto];
    }

    chunk<T>(data: Array<T>, size: number): Array<Array<T>> {
        return this._memoizedChunkFunction(data, size);
    }

    isSelected(plugType: PlugTypeDto): boolean {
        return this.plugTypeChoice && plugType === this.plugTypeChoice.plugType;
    }

    selectPlugType(plugType: PlugTypeDto) {
        if (this.disabled || !plugType) {
            return;
        }

        this.plugTypeChoice.plugType = plugType;
        this.onChange(this.plugTypeChoice);

        this.maybeNeedCable = this.plugTypeChoice.plugType.userCableRequirement === UserCableRequirement.MAYBE;
        this.noCableNeeded = this.plugTypeChoice.plugType.userCableRequirement === UserCableRequirement.NONE;
    }

    get hasPlugCable(): boolean {
        return this.plugTypeChoice && this.plugTypeChoice.hasPlugCable;
    }

    set hasPlugCable(hasPlugCable: boolean) {
        this.plugTypeChoice.hasPlugCable = hasPlugCable;
        this.onChange(this.plugTypeChoice);
    }

    public registerOnChange(onChange: (_: PlugTypeChoice) => void): void {
        this.onChange = onChange;
    }

    public registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public writeValue(value: PlugTypeChoice): void {
        this.plugTypeChoice = value;
    }

    public trackByIndex(index) {
        return index;
    }

    public trackByName(index: number, plugType: PlugTypeDto) {
        return plugType ? plugType.name : null;
    }

    ngOnDestroy(): void {
        this._memoizedChunkFunction.cache.clear();
    }
}
